/* import __COLOCATED_TEMPLATE__ from './prism-form-group.hbs'; */
// Inspired by examples at http://offirgolan.github.io/ember-cp-validations/
// ^ may be out of date now

import Component from '@ember/component';
import { computed } from '@ember/object';
import { and, not } from '@ember/object/computed';
import { isNone } from '@ember/utils';
import { htmlSafe } from '@ember/template';
import { scheduleOnce } from '@ember/runloop';

/**
 * This component renders a form-group structure.
 *
 * ```
 * <div class="prism-form-group">
 *   <label class="prism-form-label" for="my-id">Label Text</label>
 *   <input type="text" class="prism-form-control" id="my-id" value="..." />
 * </div>
 * ```
 *
 * The input line is the block rendered by the caller so it can be any input type
 * or custom component. Because the yield happens after the label it is also a
 * convenient place to put a tooltip. The component yields the fieldId so it
 * can be set on the actual field.
 *
 * To render the HTML above the code would look something like this:
 *
 * Using input helper with two-way binding
 * ```
 * {{#prism-form-group validation=model.validations.attrs.foobar
 *                     showValidation=true
 *                     fieldId="my-id"
 *                     label="Label Text" as |inputProperties|}}
 *   <Input id={{inputProperties.id}}
 *          class="prism-form-control"
 *          @value={{model.foobar}} />
 * {{/prism-form-group}}
 * ```
 *
 * Using input tag with DDAU
 * ```
 * {{#prism-form-group validation=model.validations.attrs.foobar
 *                     showValidation=true
 *                     fieldId="my-id"
 *                     label="Label Text" as |inputProperties|}}
 *   <input id={{inputProperties.id}}
 *          class="prism-form-control"
 *          value={{readonly model.foobar}}
 *          oninput={{action (mut model.foobar) value="target.value"}}>
 * {{/prism-form-group}}
 * ```
 *
 * Using a Prism input component with DDAU
 * ```
 * {{#prism-form-group validation=model.validations.attrs.foobar
 *                     showValidation=true
 *                     fieldId="my-id"
 *                     label="Label Text" as |inputProperties|}}
 *   {{prism-tin-input inputId=inputProperties.id
 *                     inputClass="prism-form-control"
 *                     value=(readonly model.foobar)
 *                     onChange=(action (mut model.foobar))}}
 * {{/prism-form-group}}
 * ```
 *
 * When the form control (any input, select, or textarea inside the form group) is
 * focused a class of `prism-form-group--has-focus` will be applied to the form group's container
 * element. This can be used to apply styles specifically to the form label element
 * which can't otherwise be applied with just CSS selectors and the `:focus` state.
 *
 * @class PrismFormGroup
 * @yield {Hash} hash
 * @yield {String} hash.id A string ID that should be applied to the field
 *   within this form group
 */
export default Component.extend({
  classNames: ['prism-form-group'],
  classNameBindings: ['_showValidationTooltip:prism-form-group--has-error', '_showFocusTooltip:prism-form-group--has-tooltip', '_fieldIsFocused:prism-form-group--has-focus'],
  /**
   * @argument fieldId
   * @type {String}
   */
  fieldId: null,
  /**
   * A validation object. Should contain the properties `isInvalid` and
   * `message`.
   *
   * @argument validation
   * @type {Object}
   */
  validation: null,
  /**
   * Whether validations should be displayed.
   *
   * If true a `prism-form-group--has-error` class will be added to the form
   * group depending on the state of the passed validation object.
   *
   * @argument showValidation
   * @type {Boolean}
   */
  showValidation: false,
  /**
   * Whether validation messages should be shown inline (vs. via a focus tooltip).
   *
   * If true the validation message will be rendered in an error div inside the form group directly.
   *
   * @argument showValidationInline
   * @type {Boolean}
   */
  showValidationInline: false,
  /**
   * A label for the form field.
   *
   * By default a label with a non-breaking space character will be rendered.
   * If this is the case the class `empty-label` is added to the form group. To
   * prevent the label element from appearing at all pass `label=false`.
   *
   * @argument label
   * @type {String|Boolean}
   */
  label: htmlSafe('&nbsp;'),
  /**
   * If true, ensures tooltips expand at very small window widths.
   *
   * @argument halfInput
   * @type {Boolean}
   */
  halfInput: false,
  /**
   * `left` or `right`. Determines position of tooltip in relation to input.
   *
   * @argument tooltipPosition
   * @type {String}
   */
  tooltipPosition: 'left',
  /**
   * `left` or `right`. Determines position of tooltip when columns collapse.
   *
   * If this property is not set then `tooltipPosition` will be used.
   *
   * @argument tooltipPositionMobile
   * @type {String}
   */
  tooltipPositionMobile: null,
  /**
   * Message to display within the focus tooltip. If null no tooltip will be
   * rendered.
   *
   * @argument focusMessage
   * @type {String}
   */
  focusMessage: null,
  /**
   * Class to apply to the focus tooltip bubble (e.g. for branding purposes).
   *
   * @argument focusMessageTooltipClass
   * @type {String}
   */
  focusMessageTooltipClass: '',
  renderErrorTooltip: true,
  renderFocusTooltip: true,
  _fieldIsFocused: false,
  _showValidationTooltip: and('showValidation', 'validation.isInvalid'),
  _showValidationErrorInTooltip: not('showValidationInline'),
  _hideValidationTooltip: not('_showValidationTooltip'),
  _showFocusTooltip: and('focusMessage', '_hideValidationTooltip'),
  displayValidationTooltip: and('_showValidationTooltip', '_fieldIsFocused', '_showValidationErrorInTooltip'),
  displayFocusTooltip: and('_showFocusTooltip', '_fieldIsFocused'),
  displayInlineValidation: and('showValidation', 'showValidationInline', 'validation.isInvalid'),
  validationMessageId: computed('fieldId', 'elementId', function () {
    return `${this.fieldId || this.elementId}__error-message`;
  }),
  focusMessageId: computed('fieldId', 'elementId', function () {
    return `${this.fieldId || this.elementId}__focus-message`;
  }),
  _tooltipPositionSame: computed('tooltipPosition', 'tooltipPositionMobile', function () {
    if (isNone(this.tooltipPositionMobile)) {
      return true;
    } else {
      return this.tooltipPosition === this.tooltipPositionMobile;
    }
  }),
  _isInvalidChanged(sender, key) {
    this._setYieldedInputAttr('aria-invalid', sender.get(key));
  },
  _setYieldedInputAttr(attr, value) {
    this.element.querySelectorAll('input,select,textarea').forEach(field => {
      field.setAttribute(attr, value);
    });
  },
  // This function and the corresponding properties are used to toggle the
  // rendering of the tooltip bubbles in the DOM.
  //
  // By re-rendering the tooltip bubble component we enable the tooltip bubble
  // animation logic to run.
  //
  // The tooltip bubble needs to be present in the DOM during error scrolling
  // so that we know what it's height will be when it appears on focus.
  _toggleTooltipRender() {
    this.setProperties({
      renderErrorTooltip: false,
      renderFocusTooltip: false
    });

    // eslint-disable-next-line ember/no-incorrect-calls-with-inline-anonymous-functions
    scheduleOnce('afterRender', this, function () {
      this.setProperties({
        renderErrorTooltip: true,
        renderFocusTooltip: true
      });
    });
  },
  errorTooltipBubbleClass: computed('tooltipPosition', 'halfInput', '_tooltipPositionSame', 'tooltipPositionMobile', function () {
    const classes = ['prism-form-error-tooltip', `prism-form-error-tooltip--${this.tooltipPosition}`];
    if (this.halfInput) {
      classes.push('prism-form-error-tooltip--half-input');
    }
    if (!this._tooltipPositionSame) {
      classes.push(`prism-form-error-tooltip--${this.tooltipPositionMobile}-mobile`);
    }
    return classes.join(' ');
  }),
  focusTooltipBubbleClass: computed('tooltipPosition', 'halfInput', '_tooltipPositionSame', 'tooltipPositionMobile', 'focusMessageTooltipClass', function () {
    const classes = ['prism-form-focus-tooltip', `prism-form-focus-tooltip--${this.tooltipPosition}`, this.focusMessageTooltipClass];
    if (this.halfInput) {
      classes.push('prism-form-focus-tooltip--half-input');
    }
    if (!this._tooltipPositionSame) {
      classes.push(`prism-form-focus-tooltip--${this.tooltipPositionMobile}-mobile`);
    }
    return classes.join(' ');
  }),
  focusIn() {
    this.set('_fieldIsFocused', true);
    this._toggleTooltipRender();
  },
  focusOut() {
    this.set('_fieldIsFocused', false);
  },
  didInsertElement() {
    if (this.validation) {
      // eslint-disable-next-line ember/no-observers
      this.addObserver('validation.isInvalid', this, '_isInvalidChanged');
      this.element.querySelector('div.prism-form-error-tooltip').setAttribute('role', 'alert');
      this._setYieldedInputAttr('aria-errormessage', this.validationMessageId);
      this._setYieldedInputAttr('aria-invalid', this.validation.isInvalid);
    }
    if (this.focusMessage) {
      this._setYieldedInputAttr('aria-describedBy', this.focusMessageId);
    }
  },
  willDestroyElement() {
    this.removeObserver('validation.isInvalid', this, '_isInvalidChanged');
  }
});